import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@goaltree/ui/components/select'
import { cn } from '@goaltree/ui/lib/utils'
import { useTranslation } from 'react-i18next'

export const SETTINGS_SECTIONS = [
  'profile',
  'appearance',
  'coach',
  'mycoach',
  'notifications',
  'today',
  'break',
  'plan',
  'apps',
  'account',
] as const
export type SettingsSectionId = (typeof SETTINGS_SECTIONS)[number]

export const rowId = (key: string) => `row-${key.replace(/\W+/g, '-')}`

export function SettingsSection({
  id,
  title,
  children,
}: {
  id: SettingsSectionId
  title: string
  children: React.ReactNode
}) {
  return (
    <section id={id} data-settings-section className="scroll-mt-4 space-y-4">
      <h2 className="text-xl font-black tracking-tight text-foreground">{title}</h2>
      {children}
    </section>
  )
}

export function SettingsGroup({
  label,
  danger,
  children,
}: {
  label?: string
  danger?: boolean
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1.5">
      {label && (
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-foreground/35">
          {label}
        </p>
      )}
      <div
        className={cn(
          'divide-y overflow-hidden rounded-xl border bg-card',
          danger ? 'divide-destructive/20 border-destructive/35' : 'divide-border',
        )}
      >
        {children}
      </div>
    </div>
  )
}

export function SettingsRow({
  id,
  icon,
  label,
  description,
  example,
  children,
  onClick,
  destructive,
}: {
  id?: string
  icon?: React.ReactNode
  label: React.ReactNode
  description?: React.ReactNode
  example?: string
  children?: React.ReactNode
  onClick?: () => void
  destructive?: boolean
}) {
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag
      id={id}
      {...(onClick ? { type: 'button' as const, onClick } : {})}
      className={cn(
        'flex w-full scroll-mt-24 items-center gap-4 px-4 py-3.5 text-left',
        onClick &&
          !destructive &&
          'cursor-pointer transition-colors hover:bg-muted/50 active:bg-muted',
        onClick && destructive && 'cursor-pointer transition-colors hover:bg-destructive/5',
      )}
    >
      {icon && (
        <span
          className={cn(
            'hidden sm:flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
            destructive ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground',
          )}
        >
          {icon}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className={cn('text-sm font-medium', destructive && 'text-destructive')}>{label}</p>
        {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
        {example && <p className="mt-0.5 text-[11px] text-muted-foreground/70">{example}</p>}
      </div>
      {children}
    </Tag>
  )
}

export function Toggle({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
  label?: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200',
        checked ? 'bg-primary' : 'bg-muted-foreground/25',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
      )}
    >
      <span
        className={cn(
          'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm ring-0 transition-transform duration-200',
          checked ? 'translate-x-5' : 'translate-x-0',
        )}
      />
    </button>
  )
}

export function formatHour(hour: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(
    new Date(2024, 0, 1, hour, 0, 0),
  )
}

export function HourSelect({
  value,
  hours,
  onChange,
  disabled,
  prefix,
}: {
  value: number
  hours: number[]
  onChange: (h: number) => void
  disabled?: boolean
  prefix?: string
}) {
  const { i18n } = useTranslation()
  return (
    <Select value={String(value)} onValueChange={(v) => onChange(Number(v))} disabled={disabled}>
      <SelectTrigger className="h-8 w-auto shrink-0 cursor-pointer rounded-lg border bg-background font-mono text-xs text-foreground">
        <SelectValue>
          {prefix ? `${prefix} ` : ''}
          {formatHour(value, i18n.language)}
        </SelectValue>
      </SelectTrigger>
      <SelectContent align="end">
        {hours.map((h) => (
          <SelectItem key={h} value={String(h)} className="text-xs">
            {formatHour(h, i18n.language)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export const range = (from: number, to: number) =>
  Array.from({ length: to - from + 1 }, (_, i) => from + i)
